/// <amd-module name="VSS/Features/Charts/Chart" />

import React = require("react");

import { ChartProps } from "Contracts";
import * as Highcharts from "highcharts";
import { format } from "VSS/Core/Util/String";
import * as Charts_Resources from "VSS/Features/Charts/Resources";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ChartTypesRegistry } from "./Internal/ChartTypesRegistry";

import "VSS/LoaderPlugins/Css!Charts/Chart";

export class Chart extends VssComponent<ChartProps> {
    // Holds the underlying Highcharts chart for interop. Not managed as React State.
    private chart: Highcharts.Chart;
    // Holds the layout container for interop. Not managed as React State.
    private chartContainer: HTMLElement;

    constructor(props: ChartProps, context?: IVssComponentContext) {
        super(props, context);
        this.chart = null;
        this.state = { chartContainer: null };
    }

    public componentDidMount() {
        this.createChart();
    }

    public componentDidUpdate() {
        if (this.props.updateOnChange) {
            this.chart.update(this.getTranslatedOptions(), true, true);
        } else {
            this.deleteChart();
            this.createChart();
        }
    }

    public componentWillUnmount() {
        this.deleteChart();
    }

    public render(): JSX.Element {
        const onRef = container => {
            this.chartContainer = container;
        };
        const layoutContainerClasses = this.props.showAccessibleForm ? "hidden" : "layout-container chart-layout-container";
        return (
            <div className="chart-component">
                <div className={layoutContainerClasses} ref={onRef} />
                {this.getTabularComponent()}
            </div>
        );
    }

    private createChart() {
        const chartOptions = this.getTranslatedOptions();
        this.chart = Highcharts.chart(this.chartContainer, chartOptions);
        this.chartContainer.setAttribute('aria-label', this.getAriaLabel());
    }

    private deleteChart() {
        if (this.chart) {
            try {
                this.chart.destroy();
            } catch {
                // Suppressing Highcharts errors during destroy (Bug #1527817)
            }
            this.chart = null;
        }
    }

    private getTranslatedOptions(): Highcharts.Options {
        const options = this.props;
        const chartHandler = ChartTypesRegistry.getInstance().instantiateType(options.chartType);
        return chartHandler.translateOptions(options);
    }

    /** Get Aria Label for Chart */
    private getAriaLabel() {
        const chartTitle = this.props.title;
        const typeLabel = ChartTypesRegistry.getInstance().getChartType(this.props.chartType).userFacingChartTypeName;
        return chartTitle
            ? format(Charts_Resources.ChartEditor_AvailableScreenreader_Format, chartTitle, typeLabel)
            : format(Charts_Resources.ChartEditor_AvailableScreenreader_NoTitle_Format, typeLabel);
    }

    private getTabularComponent(): JSX.Element {
        const chartHandler = ChartTypesRegistry.getInstance().instantiateType(this.props.chartType);
        return chartHandler.getTabularComponent(this.props);
    }
}

/**
 * A wrapper around the Chart component.
 * This component will block updates when props are unchanged.
 */
export class PureChart extends Chart {
    public shouldComponentUpdate(nextProps: ChartProps): boolean {
        let areEqual = true;

        // Shallow compare
        for (const key in nextProps) {
            if (this.props[key] !== nextProps[key]) {
                areEqual = false;
                break;
            }
        }

        // Deep compare
        if (areEqual) {
            // TODO: Need to remove pageContext from ChartProps
            const { pageContext: thisPageContext, ...thisPropsSansContext } = this.props;
            const { pageContext: nextPageContext, ...nextPropsSansContext } = nextProps;
            areEqual = JSON.stringify(thisPropsSansContext) === JSON.stringify(nextPropsSansContext);
        }

        return !areEqual;
    }
}

Components.add("vss-chart", Chart);
